import React from 'react';
import { PostCard } from './components/PostCard';
import { LiveChatView } from './components/LiveChatView';
import { SearchView } from './components/SearchView';
import { MenuView } from './components/MenuView';
import { Header } from './components/Header';
import { FeedView } from './components/FeedView';
import type { Post, VideoDisplayOptions } from './types';
import { LoadingScreen } from './components/LoadingScreen';
import { BottomNav } from './components/BottomNav';
import { TutorialOverlay } from './components/TutorialOverlay';
import { usePosts } from './hooks/usePosts';
import { DiscoverView } from './components/DiscoverView';
import { TrendsView } from './components/TrendsView';
import { ProfileView } from './components/ProfileView';
import { ChatView } from './components/ChatView';
import { SideNav } from './components/SideNav';
import { useMediaQuery } from './hooks/useMediaQuery';
import { DevelopmentBanner } from './components/DevelopmentBanner';

function App() {
  const { posts, loadNextPost, loadPreviousPost, hasMore, hasPrevious } = usePosts();
  const [selectedPost, setSelectedPost] = React.useState<Post | null>(null);
  const [showSearch, setShowSearch] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);
  const [showMenu, setShowMenu] = React.useState(false);
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [displayOptions, setDisplayOptions] = React.useState<VideoDisplayOptions>({
    centered: false, // Disabled cinema mode by default
    onCenterToggle: () => {
      setDisplayOptions(prev => ({
        ...prev,
        centered: !prev.centered
      }));
    },
    onDisplayOptionsChange: (newOptions) => {
      setDisplayOptions(prev => ({
        ...prev,
        ...newOptions,
        onCenterToggle: prev.onCenterToggle,
        onDisplayOptionsChange: prev.onDisplayOptionsChange
      }));
    }
  });
  const [showTutorial, setShowTutorial] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState('home');
  const isDesktop = useMediaQuery('(min-width: 1024px)');

  // Handle keyboard navigation for desktop
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (['ArrowUp', 'ArrowDown', 'k', 'j'].includes(e.key)) {
        // Prevent default behavior for these keys (scrolling)
        e.preventDefault();
        
        // Navigate posts with arrow keys
        if (e.key === 'ArrowUp' || e.key === 'k') {
          // Previous post
          if (hasPrevious) {
            loadPreviousPost();
            setCurrentIndex(prev => Math.max(0, prev - 1));
          }
        } else if (e.key === 'ArrowDown' || e.key === 'j') {
          // Next post
          if (hasMore) {
            loadNextPost();
            setCurrentIndex(prev => prev + 1);
          }
        }
      }
      
      // Toggle mute with 'm' key
      if (e.key === 'm') {
        setDisplayOptions(prev => ({
          ...prev,
          isMuted: !prev.isMuted
        }));
      }
      
      // Toggle fullscreen with 'f' key
      if (e.key === 'f') {
        setDisplayOptions(prev => ({
          ...prev,
          centered: !prev.centered
        }));
      }
      
      // Switch tabs with number keys
      if (e.key === '1') setActiveTab('home');
      if (e.key === '2') setActiveTab('discover');
      if (e.key === '3') setActiveTab('trending');
      if (e.key === '4') setActiveTab('chat');
      if (e.key === '5') setActiveTab('profile');
    };

    // Only add keyboard shortcuts on desktop
    if (isDesktop) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [isDesktop, hasMore, hasPrevious, loadNextPost, loadPreviousPost]);

  React.useEffect(() => {
    // Show loading screen for a minimum time to ensure content is ready
    const timer = setTimeout(() => {
      setIsLoading(false);
      // Show tutorial after content is fully visible
      // Only show tutorial on mobile
      if (!isDesktop) {
        setTimeout(() => setShowTutorial(true), 2000);
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, [isDesktop]);

  return (
    <div className="fixed inset-0 bg-black overflow-hidden">
      {isLoading && <LoadingScreen />}
      {showTutorial && <TutorialOverlay onClose={() => setShowTutorial(false)} />}
      
      <Header
        onMenuClick={() => setShowMenu(true)}
        onSearchClick={() => setShowSearch(true)}
      />
      
      {selectedPost && (
        <LiveChatView
          post={selectedPost}
          onClose={() => setSelectedPost(null)}
        />
      )}

      {showSearch && (
        <SearchView onClose={() => setShowSearch(false)} />
      )}
      
      {showMenu && (
        <MenuView
          onClose={() => setShowMenu(false)}
          displayOptions={displayOptions}
          onDisplayOptionsChange={displayOptions.onDisplayOptionsChange!}
        />
      )}
      
      <div className={`h-full flex ${isDesktop ? 'pt-16' : ''}`}>
        {isDesktop && (
          <SideNav 
            activeTab={activeTab}
            onTabChange={setActiveTab}
          />
        )}
        
        <div className={`flex-1 relative ${isDesktop ? 'ml-[240px]' : ''}`}>
          {activeTab === 'home' && (
            <FeedView
              posts={posts}
              loadNextPost={loadNextPost}
              loadPreviousPost={loadPreviousPost}
              hasMore={hasMore}
              hasPrevious={hasPrevious}
              currentIndex={currentIndex}
              setCurrentIndex={setCurrentIndex}
              displayOptions={displayOptions}
              onChatClick={setSelectedPost}
              isDesktop={isDesktop}
            />
          )}

          {activeTab === 'discover' && (
            <DiscoverView onPostSelect={setSelectedPost} />
          )}
          
          {activeTab === 'trending' && (
            <TrendsView onPostSelect={setSelectedPost} />
          )}
          
          {activeTab === 'chat' && (
            <ChatView />
          )}
          
          {activeTab === 'profile' && (
            <ProfileView onPostSelect={setSelectedPost} />
          )}
        </div>
      </div>
      
      {!isDesktop && <BottomNav activeTab={activeTab} onTabChange={setActiveTab} />}
      
      {/* Development Banner */}
      <DevelopmentBanner />
    </div>
  );
}

export default App;